import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import '../styles/Consultation.css';
import gamStar from '../assets/gam_star.png';
// import heroImg from '../assets/consultation-hero.png';
import horoscopeBg from '../assets/svg/horoscope-bg-1.svg';
import starsConsultation from '../assets/svg/stars_consultation.svg';
import paymentService from '../services/paymentService';
import bookingService from '../services/bookingService';
import metaPixelService from '../services/metaPixelService';
import { useAuth } from '../contexts/AuthContext';

// Utility function to format 1-hour time slot
const formatTimeSlot = (startHour) => {
  const formatTime = (h) => {
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    return `${displayHour}:00 ${period}`;
  };
  return `${formatTime(startHour)} - ${formatTime(startHour + 1)}`;
};

// Generate all 1-hour time slots (10 slots total)
const generateTimeSlots = () => {
  const slots = [];

  // Morning slots: 8:00 AM - 12:00 PM (4 slots)
  for (let hour = 8; hour < 12; hour++) {
    slots.push(formatTimeSlot(hour));
  }

  // Afternoon/Evening slots: 2:00 PM - 8:00 PM (6 slots)
  for (let hour = 14; hour < 20; hour++) {
    slots.push(formatTimeSlot(hour));
  }

  return slots;
};

// Check if a time slot is in the past
const isTimeSlotInPast = (slot, selectedDate) => {
  if (!selectedDate) return false;

  const consultDate = new Date(selectedDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  consultDate.setHours(0, 0, 0, 0);

  // Only check for past time if selected date is today
  if (consultDate.getTime() !== today.getTime()) {
    return false;
  }

  // Parse the slot start time (e.g., "8:00 AM - 8:30 AM")
  const slotStartTime = slot.split(' - ')[0]; // "8:00 AM"
  const [time, period] = slotStartTime.split(' '); // ["8:00", "AM"]
  const [hourStr, minuteStr] = time.split(':'); // ["8", "00"]

  let hour = parseInt(hourStr);
  const minutes = parseInt(minuteStr);

  // Convert to 24-hour format
  if (period === 'PM' && hour !== 12) {
    hour += 12;
  } else if (period === 'AM' && hour === 12) {
    hour = 0;
  }

  // Create date object for the slot time
  const slotTime = new Date();
  slotTime.setHours(hour, minutes, 0, 0);

  // Compare with current time
  return slotTime < new Date();
};

export default function Consultation({ onNavigateToSignIn }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const pendingPaymentRef = useRef(false);
  const handleSubmitRef = useRef(null);
  const initiateEventIdRef = useRef(null);
  const purchaseEventIdRef = useRef(null);

  // Form data state — pre-populate name/email from auth if available
  const [formData, setFormData] = useState({
    name: user?.username || '',
    email: user?.email || '',
    phone: '',
    dateOfBirth: '',
    timeOfBirth: '',
    placeOfBirth: '',
    message: '',
    consultationDate: '',
    consultationTimeSlot: ''
  });

  // Sync email/name if user logs in after component mounts (e.g. OAuth redirect)
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({
        ...prev,
        email: prev.email || user.email,
        name: prev.name || user.username || '',
      }));
    }
  }, [user]);

  // Auto-open booking form when navigated here from Special Offer page, or restore
  // form data saved before Google OAuth redirect
  useEffect(() => {
    if (location.state?.showForm) {
      setShowForm(true);
    }
    const savedFormData = sessionStorage.getItem('pendingConsultationFormData');
    const savedTimeSlot = sessionStorage.getItem('pendingConsultationTimeSlot');
    if (savedFormData) {
      try {
        setFormData(JSON.parse(savedFormData));
        sessionStorage.removeItem('pendingConsultationFormData');
        setShowForm(true);
      } catch (e) {}
    }
    if (savedTimeSlot) {
      setSelectedTimeSlot(savedTimeSlot);
      sessionStorage.removeItem('pendingConsultationTimeSlot');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selected time slot if it becomes invalid when date changes
  useEffect(() => {
    if (selectedTimeSlot && formData.consultationDate) {
      const isPast = isTimeSlotInPast(selectedTimeSlot, formData.consultationDate);
      const isNowBooked = bookedSlots.includes(selectedTimeSlot);
      if (isPast || isNowBooked) {
        setSelectedTimeSlot('');
        setFormData(prev => ({
          ...prev,
          consultationTimeSlot: ''
        }));
      }
    }
  }, [formData.consultationDate, selectedTimeSlot, bookedSlots]);

  // Fetch booked slots whenever the consultation date changes
  useEffect(() => {
    if (!formData.consultationDate) {
      setBookedSlots([]);
      return;
    }
    setSlotsLoading(true);
    bookingService.getBookedSlots(formData.consultationDate)
      .then(slots => setBookedSlots(slots))
      .catch(() => setBookedSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [formData.consultationDate]);

  const handleToggleForm = (show) => {
    setShowForm(show);
    setError('');
    setSuccess(false);
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTimeSlotSelect = (slot) => {
    setSelectedTimeSlot(slot);
    setFormData(prev => ({
      ...prev,
      consultationTimeSlot: slot
    }));
  };

  const validateForm = () => {
    // Personal details validation
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Please fill in all personal details');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Phone number validation (10-15 digits only)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10 || phoneDigits.length > 15) {
      setError('Phone number must be 10-15 digits');
      return false;
    }

    // Birth details validation
    if (!formData.dateOfBirth || !formData.placeOfBirth) {
      setError('Please fill in birth details');
      return false;
    }

    // Date of birth must be in the past
    const dob = new Date(formData.dateOfBirth);
    if (dob >= new Date()) {
      setError('Date of birth must be in the past');
      return false;
    }

    // Consultation date and time validation
    if (!formData.consultationDate || !formData.consultationTimeSlot) {
      setError('Please select consultation date and time');
      return false;
    }

    // Consultation date must be in the future
    const consultDate = new Date(formData.consultationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (consultDate < today) {
      setError('Consultation date must be in the future');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setError('');

    // Fire InitiateCheckout on first click only (pendingPaymentRef=true means this is an auto-resume after login)
    if (!pendingPaymentRef.current) {
      initiateEventIdRef.current = metaPixelService.generateEventId();
      metaPixelService.trackInitiateCheckout({
        value: 2550,
        currency: 'INR',
        contentName: 'Astrology Consultation',
        eventId: initiateEventIdRef.current
      });
    }

    const token = sessionStorage.getItem('authToken');
    if (!token) {
      pendingPaymentRef.current = true;
      // Persist form data so it survives a Google OAuth full-page redirect
      sessionStorage.setItem('pendingConsultationPayment', 'true');
      sessionStorage.setItem('pendingConsultationFormData', JSON.stringify(formData));
      sessionStorage.setItem('pendingConsultationTimeSlot', selectedTimeSlot);
      onNavigateToSignIn();
      return;
    }
    if (!validateForm()) return;

    setLoading(true);

    // Generate event IDs for AddPaymentInfo and Purchase deduplication
    const addPaymentEventId = metaPixelService.generateEventId();
    purchaseEventIdRef.current = metaPixelService.generateEventId();

    try {
      // Step 1: Create Razorpay order — passes Meta event IDs and PII for CAPI
      const orderResponse = await paymentService.createOrder(255000, 'INR', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone
      }, initiateEventIdRef.current, addPaymentEventId);

      // Step 2: Fire AddPaymentInfo pixel right before opening Razorpay
      metaPixelService.trackAddPaymentInfo({
        value: 2550,
        currency: 'INR',
        contentName: 'Astrology Consultation',
        eventId: addPaymentEventId
      });

      // Step 3: Open Razorpay checkout
      const options = {
        key: orderResponse.key,
        amount: orderResponse.amount,
        currency: orderResponse.currency,
        name: 'GRAHBOLI',
        description: 'Astrology Consultation',
        order_id: orderResponse.orderId,
        prefill: { name: formData.name, email: formData.email, contact: formData.phone },
        theme: { color: '#B21F25' },
        handler: async function (response) {
          // Step 4: Verify payment + create booking
          try {
            await paymentService.verifyPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              ...formData
            });
            metaPixelService.trackPurchase({
              value: 2550,
              currency: 'INR',
              contentName: 'Astrology Consultation',
              eventId: purchaseEventIdRef.current
            });
            setLoading(false);
            setSuccess(true);
            setTimeout(() => navigate('/my-bookings'), 2000);
          } catch (err) {
            setLoading(false);
            setError(err.data?.message || err.message || 'Payment verification failed. Please contact support.');
          }
        },
        modal: { ondismiss: () => setLoading(false) }
      };
      paymentService.openRazorpay(options);

    } catch (err) {
      setLoading(false);
      setError(err.data?.message || err.message || 'Could not initiate payment. Please try again.');
    }
  };

  // Keep ref current so the auth effect always calls the latest closure
  handleSubmitRef.current = handleSubmit;

  // Auto-resume payment after sign-in (email/password modal stays open) or after
  // Google OAuth redirect (page reloads, pendingPaymentRef is false but sessionStorage flag is set)
  useEffect(() => {
    if (isAuthenticated && pendingPaymentRef.current) {
      // Email/password path: modal was open, ref stayed in memory
      pendingPaymentRef.current = false;
      handleSubmitRef.current?.();
    } else if (isAuthenticated) {
      // Google OAuth path: page reloaded, check sessionStorage flag
      const pending = sessionStorage.getItem('pendingConsultationPayment');
      if (pending === 'true') {
        sessionStorage.removeItem('pendingConsultationPayment');
        pendingPaymentRef.current = true; // Prevent InitiateCheckout from re-firing
        handleSubmitRef.current?.();
      }
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="consultation-page">
      <Header activePage="services" onNavigateToSignIn={onNavigateToSignIn} />

      {/* Horoscope background with animation */}
      <img
        className="horoscope-bg"
        src={horoscopeBg}
        alt="Horoscope background design"
      />

      {/* Gam Star positioned below header */}
      <div className="decorative-star right-star">
        <img src={gamStar} alt="Decorative Star" className="gam-star" />
      </div>

      <main className="pb-12 px-4 md:px-8 lg:px-0 relative z-10">
              <div className="max-w-3xl mx-auto">
                {/* Title */}
                <h1 className="text-center text-2xl sm:text-[28px] lg:text-[56px] font-bold text-[#B21F25] mt-[60px] mb-[30px] mx-[10px]" style={{ fontFamily: 'Philosopher',  fontFeatureSettings: '"clig" 0, "liga" 0' }}>
                  Book 1-on-1 Consultation
                </h1>

                {/* Card (image + details) or Form */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
                  {!showForm ? (
                    // Original card content
                    <>
                  {/* Hero image */}
{/*                   <div className="w-full"> */}
{/*                     <img */}
{/*                       src={heroImg} */}
{/*                       alt="Astrology consultation" */}
{/*                       className="w-full h-auto object-contain" */}
{/*                       style={{ maxHeight: 'calc(100% - 10px)' }} */}
{/*                     /> */}
{/*                   </div> */}

                  {/* Content area */}
                  <div className="px-6 md:px-10 py-8">
                    <p className="text-center text-[#282828] mb-6 text-lg" style={{ fontFamily: 'Montserrat, Helvetica', fontWeight: 300 }}>
                      Get a comprehensive birth chart analysis with personalized insights
                    </p>

                    <hr className="border-t border-gray-200 my-4" />

                    <h2 className="text-center text-xl font-bold mb-4 text-[#282828]" style={{ fontFamily: 'Philosopher, Helvetica' }}>What's included</h2>

                    <div className="text-center mb-6">
                      <ul className="space-y-3 text-[#525252] inline-block text-left">
                        {[
                          'Complete birth chart analysis',
                          'Personality traits and characteristics',
                          'Career, relationship, business and vastu guidance',
                          'Future predictions and timing',
                          'Q&A session for your specific questions',
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-3">
                            <img
                              src={starsConsultation}
                              alt=""
                              className="w-5 h-5 mt-1 flex-shrink-0"
                            />
                            <span className="text-base" style={{ fontFamily: 'Montserrat, Helvetica', fontWeight: 400 }}>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="text-center mt-6">
                      <div className="inline-block text-center">
                        <div className="text-sm text-[#525252]" style={{ fontFamily: 'Montserrat, Helvetica' }}>Special price :</div>
                        <div className="mt-1 flex items-baseline justify-center gap-3">
                          <span className="text-2xl md:text-3xl font-bold text-[#B21F25]" style={{ fontFamily: 'Philosopher, Helvetica' }}>₹ 2,550</span>
                          <span className="text-sm line-through text-gray-400" style={{ fontFamily: 'Montserrat, Helvetica' }}>₹ 6,000</span>
                        </div>
                      </div>
                    </div>

                    <p className="mt-2 text-xs text-center text-[#525252]" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                      * People living outside India will be charged on different pricing.
                    </p>

                    <div className="mt-8 flex justify-center">
                      <button
                        type="button"
                        onClick={() => handleToggleForm(true)}
                        className="rounded-full bg-[#B21F25] text-white px-8 py-3 text-base font-semibold hover:brightness-95 transition-all duration-300 hover:-translate-y-0.5"
                        style={{ fontFamily: 'Montserrat, Helvetica', borderRadius: '36px', border: '2px solid #B21F25' }}
                      >
                        Book now
                      </button>
                    </div>

                    <p className="mt-4 text-center text-sm text-[#525252]" style={{ fontFamily: 'Montserrat, Helvetica', fontWeight: 400 }}>
                      Limited slots available – Book Now!
                    </p>
                  </div>
                  </>
                  ) : (
                    // Consultation Form
                    <div className="px-6 md:px-10 py-8">
                      {/* Back Button */}
                      <button
                        type="button"
                        onClick={() => handleToggleForm(false)}
                        className="mb-6 flex items-center gap-2 text-[#B21F25] hover:underline font-medium"
                        style={{ fontFamily: 'Montserrat, Helvetica' }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                        </svg>
                        Back
                      </button>

                      {/* Section 1: Personal Details */}
                      <div className="mb-8">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-8 h-8 rounded-full bg-[#B21F25] text-white flex items-center justify-center font-bold" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                            1
                          </div>
                          <h2 className="text-xl font-bold text-[#282828]" style={{ fontFamily: 'Philosopher, Helvetica' }}>
                            Fill in your personal details
                          </h2>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm text-[#525252] mb-2" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                              Name <span className="text-[#B21F25]">*</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="Jonas Khanwald"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#B21F25]"
                              style={{ fontFamily: 'Montserrat, Helvetica' }}
                            />
                          </div>

                          <div>
                            <label className="block text-sm text-[#525252] mb-2" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                              E-mail <span className="text-[#B21F25]">*</span>
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="your.email@example.com"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#B21F25]"
                              style={{ fontFamily: 'Montserrat, Helvetica' }}
                            />
                          </div>

                          <div>
                            <label className="block text-sm text-[#525252] mb-2" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                              Phone number <span className="text-[#B21F25]">*</span>
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder="1234567890"
                              pattern="[0-9]*"
                              maxLength="15"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#B21F25]"
                              style={{ fontFamily: 'Montserrat, Helvetica' }}
                            />
                            <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                              Enter 10-15 digits (spaces and special characters will be removed)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Section 2: Birth Details */}
                      <div className="mb-8">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-8 h-8 rounded-full bg-[#B21F25] text-white flex items-center justify-center font-bold" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                            2
                          </div>
                          <h2 className="text-xl font-bold text-[#282828]" style={{ fontFamily: 'Philosopher, Helvetica' }}>
                            Fill in your birth details
                          </h2>
                        </div>

                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-[#525252] mb-2" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                                Date of birth <span className="text-[#B21F25]">*</span>
                              </label>
                              <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#B21F25]"
                                style={{ fontFamily: 'Montserrat, Helvetica' }}
                              />
                            </div>

                            <div>
                              <label className="block text-sm text-[#525252] mb-2" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                                Time of birth
                              </label>
                              <input
                                type="time"
                                name="timeOfBirth"
                                value={formData.timeOfBirth}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#B21F25]"
                                style={{ fontFamily: 'Montserrat, Helvetica' }}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm text-[#525252] mb-2" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                              Place of birth <span className="text-[#B21F25]">*</span>
                            </label>
                            <input
                              type="text"
                              name="placeOfBirth"
                              value={formData.placeOfBirth}
                              onChange={handleInputChange}
                              placeholder="Mumbai, India"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#B21F25]"
                              style={{ fontFamily: 'Montserrat, Helvetica' }}
                            />
                          </div>

                          <div>
                            <label className="block text-sm text-[#525252] mb-2" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                              Message
                            </label>
                            <textarea
                              name="message"
                              value={formData.message}
                              onChange={handleInputChange}
                              placeholder="Please type your message here..."
                              rows="4"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#B21F25] resize-none"
                              style={{ fontFamily: 'Montserrat, Helvetica' }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Section 3: Consultation Date & Time */}
                      <div className="mb-8">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-8 h-8 rounded-full bg-[#B21F25] text-white flex items-center justify-center font-bold" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                            3
                          </div>
                          <h2 className="text-xl font-bold text-[#282828]" style={{ fontFamily: 'Philosopher, Helvetica' }}>
                            Select consultation date & time
                          </h2>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-semibold text-[#282828] mb-2" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                              Select date <span className="text-[#B21F25]">*</span>
                            </label>
                            <input
                              type="date"
                              name="consultationDate"
                              value={formData.consultationDate}
                              onChange={handleInputChange}
                              min={new Date().toISOString().split('T')[0]}
                              placeholder="Pick your date"
                              className="w-full md:w-64 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#B21F25]"
                              style={{ fontFamily: 'Montserrat, Helvetica' }}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-[#282828] mb-3" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                              Select time <span className="text-[#B21F25]">*</span>
                            </label>
                            {slotsLoading && (
                              <p className="text-sm text-gray-500 mb-2" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                                Checking availability...
                              </p>
                            )}
                            <div className="flex flex-wrap gap-3">
                              {generateTimeSlots().map((slot) => {
                                const isPast = isTimeSlotInPast(slot, formData.consultationDate);
                                const isBooked = bookedSlots.includes(slot);
                                const isDisabled = isPast || isBooked;

                                return (
                                  <button
                                    key={slot}
                                    type="button"
                                    onClick={() => !isDisabled && handleTimeSlotSelect(slot)}
                                    disabled={isDisabled}
                                    className={`relative px-6 py-3 rounded-lg border-2 font-medium transition-all ${
                                      isBooked
                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : isPast
                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : selectedTimeSlot === slot
                                        ? 'bg-[#B21F25] text-white border-[#B21F25]'
                                        : 'bg-white text-[#282828] border-gray-300 hover:border-[#B21F25]'
                                    }`}
                                    style={{ fontFamily: 'Montserrat, Helvetica' }}
                                  >
                                    {slot}
                                    {isBooked && (
                                      <span className="absolute -top-2 -right-2 bg-gray-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                                        Booked
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Error/Success Messages */}
                      {error && (
                        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                          {error}
                        </div>
                      )}
                      {success && (
                        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg" style={{ fontFamily: 'Montserrat, Helvetica' }}>
                          Booking created successfully! Redirecting to My Bookings...
                        </div>
                      )}

                      {/* Submit Button */}
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="w-full bg-[#B21F25] text-white py-4 rounded-lg font-semibold text-lg hover:brightness-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: 'Montserrat, Helvetica' }}
                      >
                        {loading ? 'Processing payment...' : 'Pay ₹2,550 & Book'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </main>

      <Footer />
    </div>
  );
}